////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.1                                                            //
//  Summary: Markdown renderer for content baked in at build time and news bodies //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import type { AnchorHTMLAttributes, ImgHTMLAttributes } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router';

import { Prose } from './Prose';
import { PullQuote } from './PullQuote';
import { Img } from '@/components/media/Img';

export interface MarkdownProps {
  /** Markdown source. This is either the BUILD-TIME content path (src/content/*,
   * generated from content/scrape/) or an API-sourced news body — react-markdown
   * parses it directly with no raw-HTML pass-through, so there is no sanitisation
   * step here. Admin-authored HTML (TipTap output, later) goes through
   * lib/sanitise.ts + html-react-parser instead, never through this component —
   * the two are different trust boundaries. */
  source: string;
  className?: string;
  /** Every scraped image reference is a bare `![]()` with no alt text — this callback
   * lets a call site supply meaningful, context-derived alt text per image `src`
   * (1.0.2.md §5.5 requires meaningful alt text on every content image). Falls back
   * to an empty string (decorative) if omitted; every 1.0.2 call site supplies one. */
  getImageAlt?: (src: string) => string;
}

// react-markdown's `a`/`img`/`blockquote` node props type `href`/`src` as
// `string | undefined` — narrow to '' once so the renderers below don't repeat the
// `?? ''` fallback at every call site.
function renderLink ({ href, children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement>) {
  const to = href ?? '';
  if (to.startsWith('/')) {
    return <Link to={to}>{children}</Link>;
  }
  if (to.startsWith('mailto:') || to.startsWith('tel:')) {
    return <a href={to}>{children}</a>;
  }
  return (
    <a href={to} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  );
}

export function Markdown ({ source, className, getImageAlt }: MarkdownProps) {
  const components: Components = {
    img: ({ src, alt }: ImgHTMLAttributes<HTMLImageElement>) => {
      const resolvedSrc = typeof src === 'string' ? src : '';
      const resolvedAlt = alt || getImageAlt?.(resolvedSrc) || '';
      return <Img src={resolvedSrc} alt={resolvedAlt} className="w-full rounded" />;
    },
    blockquote: ({ children }) => <PullQuote>{children}</PullQuote>,
    a: renderLink,
  };

  return (
    <Prose className={className}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{source}</ReactMarkdown>
    </Prose>
  );
}
